<template>
  <div
    class="mt-8 bg-white shadow-sm sm:rounded-t-md py-6 px-4 lg:px-6 gap-x-6 flex flex-col sm:flex-row justify-between mx-auto"
  >
    <div class="pt-2 sm:w-64">
      <p class="block text-sm leading-6 text-gray-900 sm:pt-1.5 font-semibold">Date de versement</p>
      <MonthPicker
        class="text-sm w-full rounded-md"
        :placeholder="$t('Select a time range')"
        :model-value="dateRange"
        @update:modelValue="dateRangeUpdated"
      />
    </div>
    <div class="sm:w-64">
      <MultiSelect
        id="store-selector"
        label="Magasins"
        :options="storeOptions"
        v-model="selectedStores"
        placeholder="Choisissez un ou plusieurs magasins"
      />
    </div>
  </div>
  <div class="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
    <div class="bg-white shadow-sm rounded-md py-4 px-4 lg:px-6">
      <h2 class="text-lg font-semibold">Sinistres déclarés</h2>
      <span v-if="isPresent(selectedClaims)" class="text-md font-semibold text-green-500">
        {{ selectedTotalClaims }}
      </span>
      <span v-else class="text-md font-semibold text-green-500">
        {{ totalClaims }}
      </span>
      <span class="text-sm" v-if="isPresent(selectedClaims)">
        (Compa. Réseau:
        <span :class="comparisonClass(totalAverageClaims, claimsAverage)">
          {{ comparisonPercentage(totalAverageClaims, claimsAverage) }}%
        </span>
        )
      </span>
      <div v-if="!analytics?.claims?.length" class="text-gray-500 text-center py-4">
        Aucune donnée disponible pour la période sélectionnée. Veuillez sélectionner une autre période.
      </div>
      <Table v-else :columns="claimsColumns" :data="formattedClaims" :isRowClickable="false"></Table>
    </div>

    <div class="bg-white shadow-sm rounded-md py-4 px-4 lg:px-6">
      <h2 class="text-lg font-semibold">Taux d'acceptation</h2>
      <span v-if="isPresent(selectedAcceptedClaimsRate)" class="text-md font-semibold text-green-500">
        {{ formatPercentage(selectedAverageAcceptedClaimsRate) }}
      </span>
      <span v-else class="text-md font-semibold text-green-500">
        {{ formatPercentage(acceptedClaimsRateAverage) }}
      </span>
      <span class="text-sm" v-if="isPresent(selectedAcceptedClaimsRate)">
        (Compa. Réseau:
        <span :class="comparisonClass(selectedAverageAcceptedClaimsRate, acceptedClaimsRateAverage)">
          {{ comparisonPercentage(selectedAverageAcceptedClaimsRate, acceptedClaimsRateAverage) }}%
        </span>
        )
      </span>
      <div v-if="!analytics?.claims?.length" class="text-gray-500 text-center py-4">
        Aucune donnée disponible pour la période sélectionnée. Veuillez sélectionner une autre période.
      </div>
      <Table
        v-else
        :columns="acceptedClaimsRateColumns"
        :data="formattedAcceptedClaimsRate"
        :isRowClickable="false"
      ></Table>
    </div>

    <div class="bg-white shadow-sm rounded-md py-4 px-4 lg:px-6">
      <h2 class="text-lg font-semibold">CA des sinistres</h2>
      <span v-if="isPresent(selectedClaimsAmounts)" class="text-md font-semibold text-green-500">
        {{ formatPrice(selectedTotalClaimsAmount) }}
      </span>
      <span v-else class="text-md font-semibold text-green-500">
        {{ formatPrice(totalClaimsAmount) }}
      </span>
      <span class="text-sm" v-if="isPresent(selectedClaimsAmounts)">
        (Compa. Réseau:
        <span :class="comparisonClass(totalAverageClaimsAmount, claimsAmountAverage)">
          {{ comparisonPercentage(totalAverageClaimsAmount, claimsAmountAverage) }}%
        </span>
        )
      </span>
      <Table :columns="claimsAmountColumns" :data="formattedClaimsAmounts"></Table>
    </div>
  </div>
</template>

<script>
import MultiSelect from "@/components/utils/MultiSelect.vue";
import AnalyticsService from "@/api/services/analytics.js";
import { MonthPicker, Table, formatPrice, formatPercentage, toCamelCase, isPresent } from "@estaly/ui";
import { formatDate } from "@/utils/date_formatter";
import { mapActions } from "vuex";
import { startOfMonth, endOfMonth } from "date-fns";

export default {
  components: {
    MonthPicker,
    MultiSelect,
    Table,
  },
  data() {
    return {
      dateRange: [startOfMonth(new Date()), endOfMonth(new Date())],
      analytics: null,
      selectedStores: [],
      claimsAverage: null,
      claimsAmountAverage: null,
      acceptedClaimsRateAverage: null,
      token: null,
    };
  },

  mounted() {
    const token = this.$route.query.token;
    if (!token) {
      this.notify({
        category: "simple",
        type: "error",
        title: "Un token est requis pour accéder à cette page.",
      });
      return;
    }

    this.token = token;
    this.withLoader(this.fetchAnalytics);
  },

  computed: {
    claimsColumns() {
      return [
        { label: "Position", field: "position" },
        { label: "Magasin", field: "entityName" },
        { label: "M", field: "claims" },
      ];
    },
    formattedClaims() {
      return (
        this.analytics?.claims?.map((item, index) => ({
          position: index + 1,
          entityName: item.entityName,
          claims: item.claims,
          rowClass: this.selectedStores.includes(item.entityName) ? "bg-green-200" : "",
        })) || []
      );
    },
    claimsAmountColumns() {
      return [
        { label: "Position", field: "position" },
        { label: "Magasin", field: "entityName" },
        { label: "M", field: "claimAmount" },
      ];
    },
    formattedClaimsAmounts() {
      return (
        this.analytics?.claimsAmount?.map((item, index) => ({
          position: index + 1,
          entityName: item.entityName,
          claimAmount: this.formatPrice(item.claimAmount),
          rowClass: this.selectedStores.includes(item.entityName) ? "bg-green-200" : "",
        })) || []
      );
    },
    acceptedClaimsRateColumns() {
      return [
        { label: "Position", field: "position" },
        { label: "Magasin", field: "entityName" },
        { label: "M", field: "acceptedClaimsRate" },
      ];
    },
    formattedAcceptedClaimsRate() {
      return (
        this.analytics?.acceptedClaimsRate?.map((item, index) => ({
          position: index + 1,
          entityName: item.entityName,
          acceptedClaimsRate: this.formatPercentage(item.acceptedClaimsRate),
          rowClass: this.selectedStores.includes(item.entityName) ? "bg-green-200" : "",
        })) || []
      );
    },
    params() {
      return {
        startDate: formatDate(this.dateRange[0]),
        endDate: formatDate(this.dateRange[1]),
        token: this.token,
      };
    },
    storeOptions() {
      return this.analytics?.claims
        .map((item) => ({
          label: item.entityName,
          value: item.entityName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    selectedClaims() {
      return this.analytics?.claims.filter((item) => this.selectedStores.includes(item.entityName));
    },
    selectedAcceptedClaimsRate() {
      return this.analytics?.acceptedClaimsRate.filter((item) => this.selectedStores.includes(item.entityName));
    },
    selectedClaimsAmounts() {
      return this.analytics?.claimsAmount.filter((item) => this.selectedStores.includes(item.entityName));
    },
    selectedTotalClaims() {
      return this.selectedClaims.reduce((sum, item) => sum + item.claims, 0);
    },
    selectedTotalClaimsAmount() {
      return this.selectedClaimsAmounts.reduce((sum, item) => sum + Number(item.claimAmount), 0);
    },
    selectedAverageAcceptedClaimsRate() {
      return (
        this.selectedAcceptedClaimsRate.reduce((sum, item) => sum + item.acceptedClaimsRate, 0) /
        this.selectedAcceptedClaimsRate.length
      );
    },
    totalAverageClaims() {
      return this.selectedClaims.reduce((sum, item) => sum + item.claims, 0) / this.selectedClaims.length;
    },
    totalAverageClaimsAmount() {
      return (
        this.selectedClaimsAmounts.reduce((sum, item) => sum + Number(item.claimAmount), 0) /
        this.selectedClaimsAmounts.length
      );
    },
    totalClaims() {
      return this.analytics?.claims.reduce((sum, item) => sum + item.claims, 0);
    },
    totalClaimsAmount() {
      return this.analytics?.claimsAmount.reduce((sum, item) => sum + Number(item.claimAmount), 0);
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("store", ["withLoader"]),
    formatPrice,
    formatPercentage,
    isPresent,

    comparisonPercentage(value, average) {
      if (!value || !average) return 0;
      const percentage = ((value - average) / average) * 100;
      return percentage.toFixed(2);
    },

    comparisonClass(value, average) {
      if (!value || !average) return "text-gray-500";
      return value > average ? "text-green-500" : "text-red-500";
    },

    async fetchAnalytics() {
      let response = await AnalyticsService.computeOrganizationClaimsAnalytics(this.params);
      if (response?.success) {
        const data = toCamelCase(response.data);

        this.analytics = data.analytics;
        this.claimsAverage = this.analytics.claimsAverage;
        this.claimsAmountAverage = this.analytics.claimsAmountAverage;
        this.acceptedClaimsRateAverage = this.analytics.acceptedClaimsRateAverage;
      }
    },

    dateRangeUpdated(dateRange) {
      if (!dateRange || dateRange.includes(null)) {
        this.dateRange = [];
      } else {
        this.dateRange = dateRange;
      }
      this.withLoader(this.fetchAnalytics);
    },
  },
};
</script>
